import { mountComponents, registerComponents } from "./utils/mount-components";
import { fetchPageI18n } from "./utils/fetch-i18n";
import {
  fetchAgeBucketStructure,
  combineAgeBuckets,
  type AgeBucket,
  type AgeGroupLabels,
} from "./utils/fetch-age-buckets";

import RSVPForm from "./components/RSVPForm.svelte";
import Map from "./components/Map.svelte";

type ReservationGuestPayload = {
  id?: number | null;
  position?: number | null;
  name?: string | null;
  age?: string | null;
  meal_choice?: string | null;
  diet?: string | null;
};

type ReservationPayload = {
  id?: number | null;
  access_code?: string | null;
  attendance?: string | null;
  contact_email?: string | null;
  consent?: boolean | null;
  locale?: string | null;
  guests?: ReservationGuestPayload[] | null;
};

type ReservationPrefillResult = {
  reservation: ReservationPayload | null;
  error: string | null;
};

registerComponents({
  RSVPForm,
  Map,
});

function getAccessCodeFromUrl(): string | null {
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("access_code");
    if (!code) {
      return null;
    }
    const normalized = code.trim().toLowerCase();
    return normalized.length > 0 ? normalized : null;
  } catch {
    return null;
  }
}

const PREFILL_ERROR_MESSAGES: Record<string, string> = {
  fr: "Impossible de retrouver votre réservation avec ce lien. Vous pouvez remplir le formulaire ci-dessous.",
  en: "We couldn't load your reservation from the link. You can fill in the form below.",
  el: "Δεν ήταν δυνατή η φόρτωση της κράτησής σας. Συμπληρώστε τη φόρμα παρακάτω.",
};

function getPrefillErrorMessage(lang: string): string {
  if (lang in PREFILL_ERROR_MESSAGES) {
    return PREFILL_ERROR_MESSAGES[lang as keyof typeof PREFILL_ERROR_MESSAGES];
  }
  return PREFILL_ERROR_MESSAGES.en;
}

async function fetchReservationPrefill(accessCode: string, lang: string): Promise<ReservationPrefillResult> {
  const errorMessage = getPrefillErrorMessage(lang);

  try {
    const response = await fetch(`/api/v1/reservations/${encodeURIComponent(accessCode)}/`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      credentials: "same-origin",
    });

    if (!response.ok) {
      return { reservation: null, error: errorMessage };
    }

    const data: unknown = await response.json();
    if (data && typeof data === "object") {
      return { reservation: data as ReservationPayload, error: null };
    }

    return { reservation: null, error: errorMessage };
  } catch {
    return { reservation: null, error: errorMessage };
  }
}

// (No duplicate helper definitions below; keep helpers above bootstrapComponents)

async function bootstrapComponents() {
  // Try to infer current language from URL prefix: "/<locale>/".
  const match = window.location.pathname.match(/^\/(\w{2})\//);
  const lang = match ? match[1] : "fr";

  const accessCodeFromUrl = getAccessCodeFromUrl();
  const prefillRequested = Boolean(accessCodeFromUrl);
  const { reservation: prefillReservation, error: prefillError } = accessCodeFromUrl
    ? await fetchReservationPrefill(accessCodeFromUrl, lang)
    : { reservation: null, error: null };

  // [TAXONOMY: age_groups] Load structure and i18n in parallel
  // Architecture: /api/v1/age-buckets/ (structure) + /api/v1/i18n/ (labels)
  // NO FALLBACK: If APIs fail, show error in component
  const [payload, ageBucketStructure] = await Promise.all([
    fetchPageI18n("home", lang),
    fetchAgeBucketStructure(),
  ]);

  // Extract age_groups labels from i18n payload
  let ageGroupLabels: AgeGroupLabels | null = null;
  let loadingErrorMessage: string | null = null;

  if (payload) {
    // [TAXONOMY: age_groups] Get labels and errors from payload root
    ageGroupLabels = (payload.age_groups as AgeGroupLabels) ?? null;
    loadingErrorMessage = payload.errors?.["loading_failed"] ?? null;
  }

  // Combine structure + labels into complete AgeBucket[]
  const ageBuckets: AgeBucket[] | null = combineAgeBuckets(ageBucketStructure, ageGroupLabels);

  if (payload) {
    // Inject dynamic props into RSVPForm containers when present.
    const rsvpBlock = payload.blocks.find((b) => b.id === "rsvp");

    if (rsvpBlock) {
      const data = rsvpBlock.data as Record<string, unknown>;

      const addGuestLabel = (data["add_guest_label"] as string | undefined) ?? "Add guest";
      const confirmLabel = (data["confirm_label"] as string | undefined) ?? "Confirm guests";
      const removeGuestConfirmMessage = (data["remove_guest_confirm_message"] as string | undefined)
        ?? "Are you sure you want to remove this guest?";

      const attendanceLabel = (data["attendance_label"] as string | undefined) ?? "Will you attend?";
      const attendanceAttendingLabel = (data["attendance_attending_label"] as string | undefined) ?? "I will attend";
      const attendanceNotAttendingLabel = (data["attendance_not_attending_label"] as string | undefined) ?? "I cannot attend";
      const contactEmailLabel = (data["contact_email_label"] as string | undefined) ?? "Contact email";
      const contactEmailNote = (data["contact_email_note"] as string | undefined)
        ?? "We'll use this email to confirm any updates about your reservation.";
      const successMessage = (data["rsvp_success_message"] as string | undefined)
        ?? "Thank you! Your RSVP was received.";
      const genericErrorMessage = payload.errors?.["generic_submit_error"]
        ?? "Unable to submit your RSVP. Please try again.";

      const nameLabel = data["name_label"] as string | undefined;
      const ageLabel = data["age_label"] as string | undefined;
      const childcareNote = data["childcare_note"] as string | undefined;
      const mealLabel = data["meal_label"] as string | undefined;
      const mealOptions = (data["meal_options"] as string[] | undefined) ?? [];
      const dietLabel = data["diet_label"] as string | undefined;
      const dietPlaceholder = data["diet_placeholder"] as string | undefined;
      const consentText = data["consent_text"] as string | undefined;

      document.querySelectorAll<HTMLElement>("[data-component='RSVPForm']").forEach((el) => {
        const raw = el.dataset.props;
        let base: Record<string, unknown> = {};

        if (raw) {
          try {
            const parsed = JSON.parse(raw) as unknown;
            if (parsed && typeof parsed === "object") base = parsed as Record<string, unknown>;
          } catch {
            // Ignore invalid JSON and fall back to default props.
          }
        }

        const nextProps = {
          ...base,
          csrfToken: el.dataset.csrfToken,
          addGuestLabel,
          confirmLabel,
          removeGuestConfirmMessage,
          attendanceLabel,
          attendanceAttendingLabel,
          attendanceNotAttendingLabel,
          contactEmailLabel,
          contactEmailNote,
          nameLabel,
          ageLabel,
          childcareNote,
          mealLabel,
          mealOptions,
          dietLabel,
          dietPlaceholder,
          consentText,
          successMessage,
          locale: lang,
          // [TAXONOMY: age_groups] Inject combined ageBuckets (null if loading failed)
          ageBuckets,
          // Provide localized error message for display if ageBuckets is null
          loadingErrorMessage,
          genericErrorMessage,
          prefillReservation,
          prefillError,
          prefillRequested,
        };

        el.dataset.props = JSON.stringify(nextProps);
      });
    }
  } else {
    // Payload is null - API completely failed
    // Still inject ageBuckets (likely null) and error message
    document.querySelectorAll<HTMLElement>("[data-component='RSVPForm']").forEach((el) => {
      const raw = el.dataset.props;
      let base: Record<string, unknown> = {};

      if (raw) {
        try {
          const parsed = JSON.parse(raw) as unknown;
          if (parsed && typeof parsed === "object") base = parsed as Record<string, unknown>;
        } catch {
          // Ignore invalid JSON
        }
      }

        const nextProps = {
          ...base,
          csrfToken: el.dataset.csrfToken,
          ageBuckets,
          loadingErrorMessage,
          genericErrorMessage: loadingErrorMessage, // Use loading error as fallback
          prefillReservation,
          prefillError,
          prefillRequested,
        };

      el.dataset.props = JSON.stringify(nextProps);
    });
  }

  mountComponents();
  setupContactForms();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    void bootstrapComponents();
  });
} else {
  void bootstrapComponents();
}

function hideElement(el: HTMLElement | null) {
  if (!el) return;
  el.textContent = "";
  el.setAttribute("hidden", "");
}

function showElement(el: HTMLElement | null, message: string) {
  if (!el) return;
  el.textContent = message;
  el.removeAttribute("hidden");
}

function showErrorMessage(el: HTMLElement | null, message: string, details: string[] = []) {
  if (!el) return;
  el.textContent = "";

  const lead = document.createElement("span");
  lead.textContent = message;
  el.appendChild(lead);

  if (details.length > 0) {
    const list = document.createElement("ul");
    details.forEach((detail) => {
      const trimmed = detail.trim();
      if (!trimmed) {
        return;
      }
      const item = document.createElement("li");
      item.textContent = trimmed;
      list.appendChild(item);
    });

    if (list.childElementCount > 0) {
      el.appendChild(list);
    }
  }

  el.removeAttribute("hidden");
}

function extractErrorDetails(payload: Record<string, unknown> | null): string[] {
  if (!payload) {
    return [];
  }

  const details = payload["details"];

  if (!Array.isArray(details)) {
    return [];
  }

  return details
    .map((detail) => (typeof detail === "string" ? detail.trim() : ""))
    .filter((detail) => detail.length > 0);
}

function setupContactForms() {
  document.querySelectorAll<HTMLFormElement>('[data-contact-form]').forEach((form) => {
    const successEl = form.querySelector<HTMLElement>("[data-contact-success]");
    const errorEl = form.querySelector<HTMLElement>("[data-contact-error]");
    const submitButton = form.querySelector<HTMLButtonElement>("button[type='submit']");
    const successMessage = form.dataset.successMessage ?? "Thank you! Your message has been sent.";
    const errorMessage = form.dataset.errorMessage ?? "Unable to send your message. Please try again.";

    let isSubmitting = false;

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (isSubmitting) {
        return;
      }

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      isSubmitting = true;
      submitButton?.setAttribute("disabled", "");
      hideElement(successEl);
      hideElement(errorEl);

      const name = (form.querySelector<HTMLInputElement>("input[name='name']")?.value ?? "").trim();
      const email = (form.querySelector<HTMLInputElement>("input[name='email']")?.value ?? "").trim().toLowerCase();
      const message = (form.querySelector<HTMLTextAreaElement>("textarea[name='message']")?.value ?? "").trim();
      const consent = form.querySelector<HTMLInputElement>("input[name='consent']")?.checked ?? false;
      const locale = form.querySelector<HTMLInputElement>("input[name='locale']")?.value ?? "";
      const csrfToken = form.querySelector<HTMLInputElement>("input[name='csrftoken']")?.value?.trim() ?? "";

      const payload = {
        name,
        email,
        message,
        consent,
        locale,
      };

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (csrfToken.length > 0) {
        headers["X-CSRF-Token"] = csrfToken;
      }

      try {
        const response = await fetch(form.action || "/api/v1/contact/", {
          method: "POST",
          headers,
          credentials: "same-origin",
          body: JSON.stringify(payload),
        });

        let data: unknown = null;
        try {
          data = await response.json();
        } catch {
          data = null;
        }

        if (!response.ok) {
          const payload = data && typeof data === "object" ? (data as Record<string, unknown>) : null;
          const serverMessage = payload && typeof payload["error"] === "string" ? (payload["error"] as string) : null;
          const displayMessage = serverMessage && serverMessage.length > 0 ? serverMessage : errorMessage;
          const detailMessages = extractErrorDetails(payload);
          showErrorMessage(errorEl, displayMessage, detailMessages);
          return;
        }

        showElement(successEl, successMessage);
        form.reset();
      } catch {
        showErrorMessage(errorEl, errorMessage);
      } finally {
        isSubmitting = false;
        submitButton?.removeAttribute("disabled");
      }
    });
  });
}
